import type { CoreTheme } from "sharedUI/styles";
import { darkCoreTheme } from "sharedUI/styles";
import { lookupValue } from "../../../theme-util";
import type { OverlayTheme } from "../../overlay-theme";

const shadowColorDark = (opacity: number) => `rgba(0, 0, 0, ${opacity})`;

export const darkWireCoreTheme: CoreTheme = {
    ...darkCoreTheme,

    // Neutral Primitives
    // - Default
    n0: "#000000",
    n50: "#131315",
    n100: "#1E2024",
    n200: "#2C2E33",
    n300: "#3D3F45",
    n400: "#4A4D54",
    n500: "#62656D",
    n600: "#969CA2",
    n700: "#BFC3C7",
    n800: "#EEEFF0",
    n900: "#FFFFFF",

    // System Primitives
    // - blue
    b100: "#141A29",
    b200: "#293554",
    b300: "#4B6199",
    b400: "#729AFF", // default
    b500: "#92B1FF",

    // green
    g100: "#002017",
    g200: "#003C2A",
    g300: "#007954",
    g400: "#00CB81",
    g500: "#68E7BA",

    // red
    r100: "#33110F",
    r200: "#531B19",
    r300: "#98322E",
    r400: "#FD534D",
    r500: "#FF7674",

    // yellow
    y100: "#322607",
    y200: "#533F0C",
    y300: "#977316",
    y400: "#FBBF24",
    y500: "#FFD666",

    textDark: lookupValue("n900"),
    textBase: lookupValue("n800"),
    textPale: lookupValue("n600"),
    textXpale: lookupValue("n500"),
    textDisabled: lookupValue("n400"),
    textAccent: lookupValue("primaryAccentColor"),

    iconDark: lookupValue("n800"),
    iconBase: lookupValue("n600"),
    iconPale: lookupValue("n500"),
    iconXpale: lookupValue("n400"),
    iconDisabled: lookupValue("n400A"),

    shadowSoft0: `0px 1px 0px ${shadowColorDark(0.04)}`,
    shadowSoft1: `box-shadow: 0px 1px 3px ${shadowColorDark(0.08)};`,
    shadowSoft2: `0px 6px 12px ${shadowColorDark(0.1)}`,
    shadowSoft3: `0px 10px 20px ${shadowColorDark(0.1)}`,
    shadowSoft4: `0px 24px 48px ${shadowColorDark(0.1)}`,

    shadow0: `0px 1px 0px ${shadowColorDark(0.06)}`,
    shadow1: `0px 0px 0px 1px ${shadowColorDark(0.06)}, 0px 1px 3px ${shadowColorDark(0.08)}`,
    shadow2: `0px 0px 0px 1px ${shadowColorDark(0.06)}, 0px 6px 12px ${shadowColorDark(0.08)}`,
    shadow3: `0px 0px 1px 1px ${shadowColorDark(0.06)}, 0px 10px 20px ${shadowColorDark(0.08)}`,
    shadow4: `0px 0px 1px 1px ${shadowColorDark(0.06)}, 0px 24px 48px ${shadowColorDark(0.08)}`,

    shadowDark0: `0px 1px 0px ${shadowColorDark(0.12)}`,
    shadowDark1: `0px 1px 1px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 1px 3px ${shadowColorDark(0.08)}`,
    shadowDark2: `0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 2px 4px rgba(0, 0, 0, 0.06), 0px 6px 12px ${shadowColorDark(0.08)}`,
    shadowDark3: `0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 3px 6px rgba(0, 0, 0, 0.06), 0px 10px 20px ${shadowColorDark(0.1)}`,
    shadowDark4: `0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 4px 8px rgba(0, 0, 0, 0.06), 0px 24px 48px ${shadowColorDark(0.12)}`,

    shadowXdark0: `0px 1px 0px ${shadowColorDark(0.25)}`,
    shadowXdark1: `0px 1px 1px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 1px 3px ${shadowColorDark(0.25)}`,
    shadowXdark2: `0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 2px 4px rgba(0, 0, 0, 0.06), 0px 4px 12px ${shadowColorDark(0.25)}`,
    shadowXdark3: `0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 3px 6px rgba(0, 0, 0, 0.06), 0px 10px 20px ${shadowColorDark(0.25)}`,
    shadowXdark4: `0px 6px 12px rgba(0, 0, 0, 0.06), 0px 0px 0px 1px ${shadowColorDark(
        0.08
    )}, 0px 24px 48px ${shadowColorDark(0.25)}`,
};

export const darkWireThemeOverlay: OverlayTheme = {
    overlayName: "DarkTheme",

    ...darkWireCoreTheme,

    fgColorLight: lookupValue("n500"),
    fgColorMedium: lookupValue("n600"),
    fgColorDark: lookupValue("n900"),
    fgColorOpaque: lookupValue("n900"),
    baseColor: lookupValue("n0"),
    offBaseColor: lookupValue("n200"),
    skeletonColor: lookupValue("n300"),
    linkColor: "#5db7ef",
    separatorColor: lookupValue("n200A"),
    veilColor: "rgba(0, 0, 0, 0.5)",

    primaryOverlayColor: "#000000F9",
    transparentOverlayColor: "#000000C9",
    mapTheme: "dark",

    avoidColors: [
        { color: "#15181E", contrast: 3.0, isBase: true },
        { color: "#ffffffe6", contrast: 1.2, isBase: false },
    ],

    calendar: {
        pastEventColor: "rgba(255, 255, 255, 0.12)",
        pastEventAccent: "#6c6c6c",
    },

    statusBar: {
        color: "#fff",
    },

    homeBar: {
        color: "#fff",
    },

    textField: {
        underlineColor: "rgba(255, 255, 255, 0.15)",
    },

    list: {
        topMargin: lookupValue<number>("pageMarginMedium"),
    },

    cards: {
        bgColor: "_offBaseColor",
    },

    listItem: {
        selectedColor: "rgba(255, 255, 255, 0.05)",
        selectedColorOpaque: "rgba(35, 38, 44, 1)",
        textDarkenAmount: -25,
    },

    picker: {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        hoverBgColor: "rgba(255, 255, 255, 0.1)",
        activeBgColor: "rgba(255, 255, 255, 0.12)",
        currentBgColor: "rgba(255, 255, 255, 0.15)",
        currentColor: "_fgColorDark",
    },

    navBar: {
        fadeTo: "black",
    },

    signature: {
        invert: true,
    },

    tabBar: {
        unselectedColor: "#6C6C6C",
    },

    searchBar: {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
    },

    switch: {
        compact: {
            backgroundColor: "#FFFFFF",
        },
        big: {
            backgroundColor: "_picker.activeBgColor",
            borderColor: "rgba(255, 255, 255, 0.24)",
        },
    },

    formSubmissionSuccessToast: {
        background: "rgba(33, 35, 39, 0.96)",
        translucentBackground: "rgba(33, 35, 39, 0.8)",
        checkmark: "white",
        labelColor: "#FFFFFF",
    },

    flyout: {
        overlayColor: "#00000099",
        bgColor: "#182026",
        headerFg: "white",
        headerBg: "#182026",
        headerBorderColor: "#E0E0E033",
        rightBorderColor: "transparent",
        headerImgColor: "_primaryAccentColor",
    },

    expandableOptions: {
        modal: {
            bgColor: "#000000",
        },
        options: {
            fgColorSelected: "#000000",
            fgColorUnselected: "#000000",
            bgColorUnselected: "#22252C",
        },
        button: {
            fgColorSelected: "#000000",
            fgColorUnselected: "#999999",
            bgColorUnselected: "#22252C",
        },
    },

    materialDateTimePicker: {
        header: {
            background: "#000000",
        },
        tabs: {
            background: "#000000",
        },
    },
};
